import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { captureTreatmentAction } from '../../utils/attendanceActions.ts';
import { invalidateAttendance } from './attendanceReview.ts';
import { updateError } from './user';
import { dispatch } from '../store';
import { leadTableFields } from '../../constants/fields';
import { requestMarkAsDuplicate } from '../../utils/duplicateClassification';

const initialState = {
    duplicateArchiveLoading: false,
    duplicateLeads: {
        results: []
    },
    similarLeads: {
        results: []
    },
    selectedDuplicateLeads: {
        leadIds: []
    },
    selectedSimilarLeads: {
        leadIds: []
    },
    markAsDuplicateResp: null,
    duplicateLeadsData: null
};

const slice = createSlice({
    name: 'duplicateLeads',
    initialState,
    reducers: {
        setDuplicateArchiveLoading(state, action) {
            state.duplicateArchiveLoading = action.payload;
        },
        updateDuplicateLead(state, action) {
            state.duplicateLeads.results = action.payload.results;
            state.duplicateLeads.count = action.payload.count;
        },
        updateSimilarLeads(state, action) {
            state.similarLeads.results = action.payload.results;
            state.similarLeads.count = action.payload.count;
            state.duplicateLeadsData = action.payload;
        },
        updateMarkAsDuplicate(state, action) {
            state.markAsDuplicateResp = action.payload;
        },
        updateSelectedDuplicateLeads(state, action) {
            state.selectedDuplicateLeads = action.payload;
        },
        updateSelectedSimilarLeads(state, action) {
            state.selectedSimilarLeads = action.payload;
        },
        clearTableData(state) {
            state.duplicateLeads = {
                results: []
            };
        },
        clearSimilarLeadsData(state) {
            state.similarLeads = {
                results: []
            };
        },
        resetSimilarLeadsData(state) {
            state.duplicateLeadsData = null;
        },
    }
});

export default slice.reducer;

export const { setActivities, reset, updateSelectedDuplicateLeads, setDuplicateArchiveLoading, updateSelectedSimilarLeads, clearSimilarLeadsData, updateSimilarLeads, resetSimilarLeadsData } = slice.actions;

export function getDuplicateLeads({ practiceId, searchQuery, limit = 10, offset = 0, needArchivedData = false, }, handleComplete) {
    return async () => {
        try {
            const query = `limit=${limit}&offset=${offset}&ordering=-updated_at`;
            const search = searchQuery && searchQuery.length > 0 ? `?query=${searchQuery}&${query}` : `?${query}`;
            const needArchivedDataQuery = needArchivedData === false ? '&is_archived=false' : '';
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/duplicates/${search}${needArchivedDataQuery}&${leadTableFields}`
            );
            if (response.status === 200) { dispatch(slice.actions.updateDuplicateLead(response.data)); if (handleComplete) handleComplete('success'); }
            else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
                if (handleComplete) handleComplete('error');
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            if (handleComplete) handleComplete('error');
        }
    };
}
export function getSimilarDuplicateLeads({ practiceId, searchQuery, limit = 10, offset = 0, leadId, needArchivedData = false, }) {
    return async () => {
        try {
            const query = `limit=${limit}&offset=${offset}&ordering=-updated_at`;
            const search = searchQuery && searchQuery.length > 0 ? `?query=${searchQuery}&${query}` : `?${query}`;
            const needArchivedDataQuery = needArchivedData === false ? '&is_archived=false' : '';
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/duplicates/${search}${needArchivedDataQuery}&${leadTableFields}`
            );
            if (response.status === 200) { dispatch(slice.actions.updateSimilarLeads(response.data)); }
            else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}
export function markAsDuplicate({ practiceId, leadId, handleClose }) {
    return async () => {
        try {
            const { response, feedback } = await requestMarkAsDuplicate(practiceId, leadId);
            if (response.status === 200 && feedback.valid) {
                dispatch(slice.actions.updateMarkAsDuplicate(response.data));
                dispatch(updateError(feedback));
                handleClose('finish');
            }
            else {
                dispatch(updateError(feedback));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}
export function unMarkAsDuplicate({ practiceId, leadId, handleClose }) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/unmark_as_duplicate/`, 'post'
            );
            if (response.status === 200) {
                dispatch(slice.actions.updateMarkAsDuplicate(response.data));
                dispatch(updateError({ message: 'Lead successfully unmarked as duplicate', success: true }));
                handleClose('finish');
            }
            else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}
export function bulkUnMarkAsDuplicate({ practiceId, leadId, isLastIndex, handleClose }) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/unmark_as_duplicate/`, 'post'
            );
            if (response.status === 200) {
                dispatch(slice.actions.updateMarkAsDuplicate(response.data));
                dispatch(updateError({ message: 'Lead successfully unmarked as duplicate', success: true }));
                if (isLastIndex) {
                    handleClose('finish');
                }
            }
            else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

export function bulkArchivedOrRestore(practiceId, data, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/bulk_archive_restore/`, 'post', data);
            if (response.status === 200) {
                handleSuccess('finish', data?.object_ids);
                dispatch(updateError({ message: data.request_type === 'ARCHIVE' ? 'Lead will archived in 5 minutes.' : 'Restore completed successfully.', success: true }));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
                handleSuccess('');
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

export function suggestMergePrimary({ practiceId, leadId }) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/suggest_merge_primary/`
            );
            if (response.status === 200) {
                return response.data;
            }
            dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            return null;
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            return null;
        }
    };
}

// Fetches the full lead object for a counterpart that was only known by id
// (e.g. a merge suggestion that wasn't in the currently loaded table page),
// so conflict review can run against real contact/attribution data instead
// of a stub.
export function fetchLeadForMerge({ practiceId, leadId }) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`);
            if (response.status === 200) {
                return response.data;
            }
            dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            return null;
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            return null;
        }
    };
}

export function mergeLeads({ practiceId, primaryId, secondaryId, matchReason, fieldDecisions, handleClose }) {
    return async () => {
        try {
            const payload = { secondary_lead_id: secondaryId };
            if (matchReason) payload.match_reason = matchReason;
            if (fieldDecisions && Object.keys(fieldDecisions).length > 0) payload.field_decisions = fieldDecisions;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${primaryId}/merge/`, 'post', payload
            );
            if (response.status === 200) {
                dispatch(updateError({ message: response.data?.message || 'Leads merged successfully', success: true }));
                // The response body carries merge_history_id / transferred_counts so the
                // caller can show the exact effect of the merge.
                if (handleClose) handleClose('finish', response.data);
            } else {
                // 400/409 return { error: '...' } — surface the server wording verbatim.
                dispatch(updateError({ message: response?.data?.error || JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

export const completedToStart = (params) => async () => {
    const { practiceId, leadId, id, handleClose } = params;
    const data = {
        id,
        lead_pk: leadId,
        practice_pk: practiceId
    };
    try {
        const outcome = await captureTreatmentAction({ practiceId, leadId, treatmentId: id, targetStatus: 'TREATMENT_STARTED', extraData: data });
        if (outcome.status !== 'success') return;
        const response = outcome.data;
        if (response.status === 200) {
            dispatch(invalidateAttendance({ practiceId, leadId, treatmentId: id }));
            handleClose('finish');
            dispatch(updateError({ message: 'Lead status updated successfully', success: true }));
        }
        else {
            dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
        }
    } catch (error) {
        dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
    }
};
